import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, Row, Alert } from 'react-bootstrap';
import jwt_decode from 'jwt-decode';
import TelecmiApi from '../../../api/TelecmiApi';
import ObjectConfigApi from '../../../api/ObjectConfigApi';
import PicklistApi from '../../../api/PicklistApi';
import LookupLayout from '../../layout/LookupLayout';

function normalizePicklistRows(raw) {
  if (raw == null) return [];
  let rows = raw;
  if (typeof rows === 'string') {
    try {
      rows = JSON.parse(rows.replace(/\\n/g, ''));
    } catch {
      return [];
    }
  }
  if (!Array.isArray(rows)) return [];
  return rows
    .map((item) => {
      const value =
        item?.key ?? item?.value_key ?? item?.valueKey ?? item?.value ?? '';
      const label =
        item?.label ??
        item?.value_label ??
        item?.valueLabel ??
        item?.value ??
        value;
      const v = value != null && String(value).trim() !== '' ? String(value).trim() : '';
      if (!v) return null;
      return { value: v, label: String(label || v).trim() || v };
    })
    .filter(Boolean);
}

async function optionsForLeadPicklistField(field) {
  if (!field) return [];
  const fromConfig = normalizePicklistRows(
    field.picklist_values_data ??
      field.picklistValuesData ??
      field.picklist_values ??
      field.picklistValues
  );
  if (fromConfig.length > 0) {
    return fromConfig.sort(
      (a, b) =>
        (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' })
    );
  }
  const fid = field.id || field.field_id;
  if (!fid) return [];
  try {
    const picklistApi = new PicklistApi();
    const res = await picklistApi.getActivePicklistValues(fid);
    const payload = res?.data;
    const values = Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload)
        ? payload
        : [];
    return values
      .map((item) => {
        const value =
          item?.value_key ?? item?.valueKey ?? item?.key ?? item?.value ?? '';
        const label =
          item?.value_label ?? item?.valueLabel ?? item?.label ?? item?.value ?? value;
        const v = value != null && String(value).trim() !== '' ? String(value).trim() : '';
        if (!v) return null;
        return { value: v, label: String(label || v).trim() || v };
      })
      .filter(Boolean)
      .sort((a, b) =>
        (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' })
      );
  } catch {
    return [];
  }
}

const TelecmiSettingsManager = () => {
  const isAdmin = useMemo(() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;
      const decoded = jwt_decode(token);
      return String(decoded?.role_name || '').toUpperCase() === 'ADMIN';
    } catch {
      return false;
    }
  }, []);

  const [defaultCrmUserId, setDefaultCrmUserId] = useState('');
  const [defaultCrmUserLabel, setDefaultCrmUserLabel] = useState('');
  const [secret, setSecret] = useState('');
  const [appid, setAppid] = useState('');
  const [apiBaseUrl, setApiBaseUrl] = useState('');
  const [click2callUrl, setClick2callUrl] = useState('');
  const [playUrl, setPlayUrl] = useState('');
  const [callerid, setCallerid] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [requireTelecmiLogin, setRequireTelecmiLogin] = useState(false);
  const [secretConfigured, setSecretConfigured] = useState(false);
  const [leadStatus, setLeadStatus] = useState('');
  const [leadSource, setLeadSource] = useState('');
  const [leadStatusOptions, setLeadStatusOptions] = useState([]);
  const [leadSourceOptions, setLeadSourceOptions] = useState([]);
  const [leadPicklistsLoading, setLeadPicklistsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ variant: '', text: '' });
  const [cdrWebhookPath, setCdrWebhookPath] = useState('');
  const [liveWebhookPath, setLiveWebhookPath] = useState('');
  const [webhookCopyHint, setWebhookCopyHint] = useState('');

  const leadStatusSelectOptions = useMemo(() => {
    const opts = [...leadStatusOptions];
    if (
      leadStatus &&
      !opts.some((o) => String(o.value) === String(leadStatus))
    ) {
      opts.push({ value: leadStatus, label: `${leadStatus} (current)` });
    }
    return opts;
  }, [leadStatusOptions, leadStatus]);

  const leadSourceSelectOptions = useMemo(() => {
    const opts = [...leadSourceOptions];
    if (
      leadSource &&
      !opts.some((o) => String(o.value) === String(leadSource))
    ) {
      opts.push({ value: leadSource, label: `${leadSource} (current)` });
    }
    return opts;
  }, [leadSourceOptions, leadSource]);

  const load = useCallback(async () => {
    setLoading(true);
    setMessage({ variant: '', text: '' });
    try {
      const res = await TelecmiApi.fetchSettings();
      const data = res?.data?.data;
      if (res?.data?.success && data) {
        setDefaultCrmUserId(data.default_user_id || '');
        setDefaultCrmUserLabel(data.default_user_label || '');
        setCallerid(data.callerid != null ? String(data.callerid) : '');
        setAppid(data.appid != null ? String(data.appid) : '');
        setApiBaseUrl(data.api_base_url != null ? String(data.api_base_url) : '');
        setClick2callUrl(data.click2call_url != null ? String(data.click2call_url) : '');
        setPlayUrl(data.play_url != null ? String(data.play_url) : '');
        setIsActive(data.is_active === true);
        setRequireTelecmiLogin(data.require_telecmi_login === true);
        setSecretConfigured(data.secret_configured === true);
        setLeadStatus(data.lead_status != null ? String(data.lead_status) : '');
        setLeadSource(data.lead_source != null ? String(data.lead_source) : '');
        setCdrWebhookPath(data.cdr_webhook_path || '');
        setLiveWebhookPath(data.live_webhook_path || '');
        setSecret('');
      } else {
        setMessage({
          variant: 'danger',
          text: res?.data?.message || 'Failed to load TeleCMI settings.',
        });
      }
    } catch (e) {
      setMessage({
        variant: 'danger',
        text:
          e?.response?.data?.message ||
          e?.message ||
          'Failed to load TeleCMI settings.',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLeadPicklistsLoading(true);
      try {
        const res = await ObjectConfigApi.fetchConfigObjectByNameWithFields('lead');
        const cfg = res?.data ?? res;
        const fields = cfg?.fields || [];
        const byName = (name) =>
          fields.find(
            (f) => String(f?.field_name || f?.fieldName || '').toLowerCase() === name
          );
        const statusField = byName('lead_status');
        const sourceField = byName('lead_source');
        const [statusOpts, sourceOpts] = await Promise.all([
          optionsForLeadPicklistField(statusField),
          optionsForLeadPicklistField(sourceField),
        ]);
        if (!cancelled) {
          setLeadStatusOptions(statusOpts);
          setLeadSourceOptions(sourceOpts);
        }
      } catch (e) {
        console.warn('TeleCMI settings: failed to load Lead picklists', e?.message || e);
        if (!cancelled) {
          setLeadStatusOptions([]);
          setLeadSourceOptions([]);
        }
      } finally {
        if (!cancelled) setLeadPicklistsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;
    const calleridTrim = String(callerid || '').trim();
    if (!calleridTrim) {
      setMessage({ variant: 'danger', text: 'Default caller ID is required.' });
      return;
    }
    setSaving(true);
    setMessage({ variant: '', text: '' });
    try {
      const body = {
        default_user_id: defaultCrmUserId.trim() === '' ? null : defaultCrmUserId.trim(),
        is_active: isActive,
        require_telecmi_login: requireTelecmiLogin,
        callerid: calleridTrim,
        appid: appid.trim() === '' ? null : appid.trim(),
        api_base_url: apiBaseUrl.trim() === '' ? null : apiBaseUrl.trim(),
        click2call_url: click2callUrl.trim() === '' ? null : click2callUrl.trim(),
        play_url: playUrl.trim() === '' ? null : playUrl.trim(),
        lead_status: leadStatus.trim() === '' ? null : leadStatus.trim(),
        lead_source: leadSource.trim() === '' ? null : leadSource.trim(),
      };
      if (secret.trim() !== '') {
        body.secret = secret.trim();
      }
      const res = await TelecmiApi.saveSettings(body);
      if (res?.data?.success) {
        setMessage({ variant: 'success', text: 'TeleCMI settings saved.' });
        setSecret('');
        const data = res?.data?.data;
        if (data) {
          setDefaultCrmUserId(data.default_user_id || '');
          setDefaultCrmUserLabel(data.default_user_label || '');
          setCallerid(data.callerid != null ? String(data.callerid) : '');
          setAppid(data.appid != null ? String(data.appid) : '');
          setApiBaseUrl(data.api_base_url != null ? String(data.api_base_url) : '');
          setClick2callUrl(data.click2call_url != null ? String(data.click2call_url) : '');
          setPlayUrl(data.play_url != null ? String(data.play_url) : '');
          setIsActive(data.is_active === true);
          setRequireTelecmiLogin(data.require_telecmi_login === true);
          setSecretConfigured(data.secret_configured === true);
          setLeadStatus(data.lead_status != null ? String(data.lead_status) : '');
          setLeadSource(data.lead_source != null ? String(data.lead_source) : '');
          setCdrWebhookPath(data.cdr_webhook_path || '');
          setLiveWebhookPath(data.live_webhook_path || '');
        }
      } else {
        setMessage({
          variant: 'danger',
          text: res?.data?.message || 'Save failed.',
        });
      }
    } catch (e) {
      setMessage({
        variant: 'danger',
        text:
          e?.response?.data?.message ||
          e?.message ||
          'Save failed.',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-3">Loading…</div>;
  }

  return (
    <div className="p-3" style={{ maxWidth: 720 }}>
      <h4 className="mb-3">TeleCMI</h4>
      <p className="text-muted small">
        Configure the organization TeleCMI app secret (stored encrypted). Each CRM user sets their
        TeleCMI User ID on the User record when the TeleCMI package is enabled.
        Enable the TeleCMI package on your subscription in the admin panel first.
      </p>

      {!isAdmin && (
        <Alert variant="info">
          Only administrators can change TeleCMI credentials.
        </Alert>
      )}

      {message.text && (
        <Alert variant={message.variant || 'info'} className="mt-2">
          {message.text}
        </Alert>
      )}

      <Form onSubmit={handleSave}>
        <Card className="mt-3">
          <Card.Body>
            <Form.Group className="mb-3" controlId="telecmi-active">
              <Form.Check
                type="switch"
                id="telecmi-is-active"
                label="TeleCMI Active"
                checked={isActive}
                onChange={(ev) => setIsActive(ev.target.checked)}
                disabled={!isAdmin}
              />
              <Form.Text className="text-muted">
                When off, users cannot place calls via TeleCMI even if credentials exist.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="telecmi-require-login">
              <Form.Check
                type="switch"
                id="telecmi-require-login"
                label="Require TeleCMI softphone login for outbound calls"
                checked={requireTelecmiLogin}
                onChange={(ev) => setRequireTelecmiLogin(ev.target.checked)}
                disabled={!isAdmin}
              />
              <Form.Text className="text-muted">
                When on, calls use your TeleCMI session token (sign in via the header softphone).
                When off, outbound calls use the organization secret and each user’s TeleCMI User ID on their profile.
              </Form.Text>
            </Form.Group>

            <Row className="g-3">
              <Col md={12}>
                <Form.Group controlId="telecmi-default-crm-user">
                  <Form.Label>Default CRM User For Call Webhooks</Form.Label>
                  <LookupLayout
                    objectName="user"
                    searchFields="firstname,lastname, username"
                    displayFields="firstname,lastname, username"
                    value={defaultCrmUserId || null}
                    defaultLabel={defaultCrmUserLabel || ''}
                    onChange={(id, label) => {
                      setDefaultCrmUserId(id || '');
                      setDefaultCrmUserLabel(label || '');
                    }}
                    placeholder="Search users…"
                    disabled={!isAdmin}
                  />
                  <Form.Text className="text-muted">
                    Incoming CDR webhooks set telecmi_log owner, created by, and last modified by to
                    this user when configured.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group controlId="telecmi-appid">
                  <Form.Label>TeleCMI App ID (Appid)</Form.Label>
                  <Form.Control
                    value={appid}
                    onChange={(ev) => setAppid(ev.target.value)}
                    placeholder="e.g. 123456"
                    disabled={!isAdmin}
                    autoComplete="off"
                  />
                  <Form.Text className="text-muted">
                    Used for recording playback. If blank, playback falls back to your user’s TeleCMI User ID.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group controlId="telecmi-api-base-url">
                  <Form.Label>API Base URL</Form.Label>
                  <Form.Control
                    value={apiBaseUrl}
                    onChange={(ev) => setApiBaseUrl(ev.target.value)}
                    placeholder="https://rest.telecmi.com"
                    disabled={!isAdmin}
                    autoComplete="off"
                  />
                  <Form.Text className="text-muted">
                    TeleCMI REST host. Leave blank to use the built-in default or server{' '}
                    <code className="small">TELECMI_API_BASE_URL</code>.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group controlId="telecmi-click2call-url">
                  <Form.Label>Click2Call URL (Optional Override)</Form.Label>
                  <Form.Control
                    value={click2callUrl}
                    onChange={(ev) => setClick2callUrl(ev.target.value)}
                    placeholder="https://rest.telecmi.com/v2/webrtc/click2call"
                    disabled={!isAdmin}
                    autoComplete="off"
                  />
                  <Form.Text className="text-muted">
                    Full URL for outbound click-to-call. If blank, defaults to {' '}
                    <code className="small">{'{API Base URL}'}/v2/webrtc/click2call</code>.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group controlId="telecmi-play-url">
                  <Form.Label>Play URL (Optional Override)</Form.Label>
                  <Form.Control
                    value={playUrl}
                    onChange={(ev) => setPlayUrl(ev.target.value)}
                    placeholder="https://rest.telecmi.com/v2/play"
                    disabled={!isAdmin}
                    autoComplete="off"
                  />
                  <Form.Text className="text-muted">
                    Recording play endpoint (no query string; app sends appid, secret, file). If blank,
                    defaults to <code className="small">{'{API Base URL}'}/v2/play</code>.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group controlId="telecmi-secret">
                  <Form.Label>App Secret</Form.Label>
                  <Form.Control
                    type="password"
                    value={secret}
                    onChange={(ev) => setSecret(ev.target.value)}
                    placeholder={
                      secretConfigured
                        ? 'Leave blank to keep existing secret'
                        : 'Required when TeleCMI is active'
                    }
                    disabled={!isAdmin}
                    autoComplete="new-password"
                  />
                  {secretConfigured && (
                    <Form.Text className="text-muted">
                      A secret is already stored. Enter a new value only to replace it.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group controlId="telecmi-callerid">
                  <Form.Label>Default Caller ID</Form.Label>
                  <Form.Control
                    value={callerid}
                    onChange={(ev) => setCallerid(ev.target.value)}
                    placeholder="e.g. 911203134432"
                    disabled={!isAdmin}
                    autoComplete="off"
                    required
                  />
                  <Form.Text className="text-muted">
                    Required. Must be numeric.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group controlId="telecmi-lead-status">
                  <Form.Label>Default Lead Status</Form.Label>
                  <Form.Select
                    value={leadStatus}
                    onChange={(ev) => setLeadStatus(ev.target.value)}
                    disabled={!isAdmin || leadPicklistsLoading}
                  >
                    <option value="">— None —</option>
                    {leadStatusSelectOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Values come from the Lead object&apos;s Lead Status picklist (stored value key).
                    {leadPicklistsLoading ? ' Loading options…' : ''}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group controlId="telecmi-lead-source">
                  <Form.Label>Default Lead Source</Form.Label>
                  <Form.Select
                    value={leadSource}
                    onChange={(ev) => setLeadSource(ev.target.value)}
                    disabled={!isAdmin || leadPicklistsLoading}
                  >
                    <option value="">— None —</option>
                    {leadSourceSelectOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Values come from the Lead object&apos;s Lead Source picklist (stored value key).
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mt-3">
          <Card.Body>
            <Card.Title as="h6">Webhook URLs</Card.Title>
          <p className="text-muted small mb-3">
            Paste these into the TeleCMI portal. The CDR URL is for final call detail records (stored in CRM). The Live URL
            is for in-call progress events (waiting, answered, hangup); those are pushed to connected CRM users via Socket.IO
            and can open the live status modal. Use the same webhook secret your API expects (for example{' '}
            <code className="small">TELECMI_WEBHOOK_SECRET</code>).
          </p>
          <Form.Group className="mb-3">
            <Form.Label>CDR webhook</Form.Label>
            <div className="d-flex gap-2 flex-wrap">
              <Form.Control
                readOnly
                value={cdrWebhookPath}
                placeholder={cdrWebhookPath ? undefined : 'Not available (sign in with a company context).'}
              />
              <Button
                type="button"
                variant="outline-secondary"
                disabled={!cdrWebhookPath}
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(cdrWebhookPath);
                    setWebhookCopyHint('cdr');
                    window.setTimeout(() => setWebhookCopyHint(''), 2000);
                  } catch {
                    setMessage({ variant: 'warning', text: 'Could not copy CDR URL.' });
                  }
                }}
              >
                Copy
              </Button>
            </div>
            {webhookCopyHint === 'cdr' && (
              <Form.Text className="text-success">Copied.</Form.Text>
            )}
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label>Live / call progress webhook</Form.Label>
            <div className="d-flex gap-2 flex-wrap">
              <Form.Control
                readOnly
                value={liveWebhookPath}
                placeholder={liveWebhookPath ? undefined : 'Not available (sign in with a company context).'}
              />
              <Button
                type="button"
                variant="outline-secondary"
                disabled={!liveWebhookPath}
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(liveWebhookPath);
                    setWebhookCopyHint('live');
                    window.setTimeout(() => setWebhookCopyHint(''), 2000);
                  } catch {
                    setMessage({ variant: 'warning', text: 'Could not copy Live URL.' });
                  }
                }}
              >
                Copy
              </Button>
            </div>
            {webhookCopyHint === 'live' && (
              <Form.Text className="text-success">Copied.</Form.Text>
            )}
          </Form.Group>
          </Card.Body>
        </Card>

        {isAdmin && (
          <div className="mt-3 pt-3 border-top d-flex gap-2 flex-wrap">
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button type="button" variant="outline-secondary" onClick={load} disabled={saving}>
              Reload
            </Button>
          </div>
        )}
      </Form>
    </div>
  );
};

export default TelecmiSettingsManager;
